import React from 'react';
import { X } from 'lucide-react';

/**
 * Minimal auth modal — Google Sign-In only (clean break from email/password).
 *
 * Flow:
 * 1. User clicks "Continue with Google"
 * 2. We redirect to Emergent's Google OAuth endpoint with our return URL
 * 3. Emergent redirects back to /auth/callback#session_id=xxx
 * 4. AuthCallback page exchanges that for our session_token
 */
export default function AuthModal({ isOpen, onClose, onSuccess }) {
  if (!isOpen) return null;

  const handleGoogleSignIn = () => {
    const returnUrl = `${window.location.origin}/auth/callback`;
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(returnUrl)}`;
    window.location.href = authUrl;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="auth-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-md rounded-2xl bg-[#1A2035] border border-[#2A3550] p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          data-testid="auth-modal-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">Welcome to Visual Atmos</h2>
          <p className="text-gray-400 text-sm">Sign in with Google to get <span className="text-[#FFB238] font-semibold">40 free credits every day</span>.</p>
        </div>

        <button
          onClick={handleGoogleSignIn}
          data-testid="google-signin-button"
          className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-lg bg-white text-gray-800 font-semibold text-base hover:bg-gray-100 transition-colors shadow-md"
        >
          <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
            <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
            <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
            <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
          </svg>
          <span>Continue with Google</span>
        </button>

        <p className="text-xs text-gray-500 text-center mt-6">
          By continuing you agree to our{' '}
          <a href="/terms" className="underline hover:text-gray-300">Terms</a> and{' '}
          <a href="/privacy" className="underline hover:text-gray-300">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
